/**
 * Simple Bundles BYOB - modal option improvements (TomboyX)
 * ---------------------------------------------------------
 * 1. Hides dropdowns that only have one choice (e.g. Color, since each print
 *    is its own product). The value still gets submitted, it's just hidden.
 * 2. Turns the remaining dropdowns (e.g. Size) into buttons.
 * 3. Remembers the last size picked and preselects it in the next modal,
 *    so shoppers building a 5-pack only pick their size once.
 * 4. Style filter above the grid, built from the collections listed in
 *    CONFIG.collections. A chip only shows if at least one product in the
 *    grid is in that collection. Picking more than one chip shows either.
 *    Shows two rows of chips, with a "+ More" toggle if there are more.
 * 5. Search box that filters the grid by product name (e.g. "bat").
 * 5b. Size filter: pick a size to see only products with it in stock.
 *    Also preselects that size in the product modals.
 * 6. Card polish: hover lift, rounded images, result count, empty state.
 * 7. Per-bundle settings from product metafields set in Shopify admin
 *    (custom.byob_search_text, custom.byob_filter), via theme.liquid.
 * 8. After "Add bundle to cart", opens the theme's cart drawer instead of
 *    going to the cart page.
 *
 * Drop-in replacement for byob-swatches.js. Same theme.liquid line:
 *   <script src="{{ 'byob-swatches.js' | asset_url }}" defer></script>
 * No Liquid template needed (templates/product.swatches.liquid can be removed).
 *
 * The widget is Stencil-based and re-renders, so everything here is
 * idempotent and re-applied via MutationObserver.
 */
(() => {
  'use strict';

  if (window.__byobOptionsLoaded) return;
  window.__byobOptionsLoaded = true;

  // Off switch for troubleshooting: add ?sb_custom=off to the page URL and
  // none of these customisations run, so the bundle behaves as the app
  // ships it. Use it to check whether an issue comes from this script.
  try {
    if (new URLSearchParams(window.location.search).get('sb_custom') === 'off') {
      console.log('[BYOB options] Switched off by ?sb_custom=off');
      return;
    }
  } catch (e) { /* ignore */ }

  /* ------------------------------------------------------------------ */
  /* CONFIG                                                              */
  /* ------------------------------------------------------------------ */

  const CONFIG = {
    hideSingleChoice: true,   // hide dropdowns with only one option
    buttons: true,            // turn other dropdowns into buttons
    rememberChoice: true,     // preselect the last size picked

    // Button colors. Accent is taken from the store's "Select options" blue.
    accent: '#346fb0',
    border: '#d1d1d1',

    storageKey: 'byob:lastChoice',

    /* ---- Filter bar ---- */
    filters: true,
    search: true,
    cardPolish: true,

    // Style collections to offer as filter chips, in this order.
    // handle = the last part of the collection URL, e.g.
    //   tomboy-x.myshopify.com/collections/iconic-brief -> 'iconic-brief'
    // Label is optional; the collection title is used if left out.
    // Chips with no products in this bundle are hidden automatically,
    // so it's safe to list extra collections here.
    collections: [
      { handle: '4-5-trunks' },
      { handle: '6-boxer-briefs' },
      { handle: '9-boxer-brief' },
      { handle: 'boy-shorts' },
      { handle: 'bamboo-rib' },
      { handle: 'iconic-brief' }
    ],

    allLabel: 'All',
    searchPlaceholder: 'Search',

    // Where the style chips come from:
    //   'collections' = the collections listed above
    //   'metafield'   = a product metafield, read through the small
    //                   templates/product.byob-filter.liquid template.
    //                   One chip per value found (e.g. Print, Solid).
    styleSource: 'collections',
    metafieldView: 'byob-filter',
    // Optional nicer chip names for metafield values, e.g. { Print: 'Prints' }
    metafieldLabels: { Print: 'Prints', Solid: 'Solids' },

    // Per-bundle settings. Key = the bundle's product handle (the last part
    // of its URL). Anything set here replaces the default above for that
    // bundle only.
    bundles: {
      'boy-shorts-bundle': {
        searchPlaceholder: 'Search boy shorts',
        styleSource: 'metafield',
        styleLabel: 'Prints or Solids'
      }
    },

    // Size filter: shows only products with that size in stock.
    // Uses the stock info Simple Bundles already puts on the page.
    sizeFilter: true,
    sizeOptionName: 'Size',    // option name to filter by
    sizeLabel: 'Size',
    styleLabel: 'Style',
    rowsCollapsed: 2,          // chip rows shown before "+ More"

    // Keep the filter bar pinned while scrolling. If the store header is
    // also pinned, set stickyTop to the header height in px (e.g. 70).
    sticky: true,
    stickyTop: 0,

    // After "Add bundle to cart", open the theme's cart drawer instead of
    // going to the cart page. Built for Maestrooo themes (Impact, Prestige).
    // Browsers that can't do this still go to the cart page, as before.
    openCartDrawer: true,

    // Set true while testing to see what the script is doing in console
    debug: false
  };

  /* ------------------------------------------------------------------ */
  /* HELPERS                                                             */
  /* ------------------------------------------------------------------ */

  const log = (...args) => { if (CONFIG.debug) console.log('[BYOB options]', ...args); };

  // Apply this bundle's own settings, if it has any
  try {
    const m = window.location.pathname.match(/\/products\/([^/?#]+)/);
    const pageHandle = m ? decodeURIComponent(m[1]).toLowerCase() : null;
    if (pageHandle && CONFIG.bundles && CONFIG.bundles[pageHandle]) {
      Object.assign(CONFIG, CONFIG.bundles[pageHandle]);
      log('Using settings for bundle:', pageHandle);
    }
  } catch (e) { /* ignore */ }

  // Settings the merchant fills in on the bundle product in Shopify admin
  // (product metafields). theme.liquid prints them, HTML-escaped, as data
  // attributes on <div id="sb-byob-settings">. These win over everything above.
  //   custom.byob_search_text  e.g. "Search boy shorts"
  //   custom.byob_filter       "Prints or Solids", "Style" or "None"
  try {
    const el = document.getElementById('sb-byob-settings');
    const s = el ? { searchPlaceholder: el.dataset.search, filter: el.dataset.filter } : {};
    if (s.searchPlaceholder) CONFIG.searchPlaceholder = String(s.searchPlaceholder);
    const f = s.filter ? String(s.filter).trim().toLowerCase() : '';
    if (f === 'prints or solids') {
      Object.assign(CONFIG, { filters: true, styleSource: 'metafield', styleLabel: 'Prints or Solids' });
    } else if (f === 'style') {
      Object.assign(CONFIG, { filters: true, styleSource: 'collections', styleLabel: 'Style' });
    } else if (f === 'none') {
      CONFIG.filters = false;
    }
    if (s.searchPlaceholder || f) log('Using admin settings:', s);
  } catch (e) { /* ignore */ }

  const SEL = {
    host:        'byob-bundle',
    dialog:      'dialog.byob-options-modal',
    select:      'select.byob-bundle__product-variant-select',
    section:     '.byob-bundle__product-section',
    grid:        '.byob-bundle__product-grid',
    card:        '.byob-bundle__product-card',
    cardTitle:   '.byob-bundle__product-card-title-text',
    detailsLink: 'a.byob-options-modal__details-link'
  };

  const debounce = (fn, ms) => {
    let t;
    return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); };
  };

  const getRemembered = () => {
    if (!CONFIG.rememberChoice) return null;
    try { return sessionStorage.getItem(CONFIG.storageKey); } catch (e) { return null; }
  };

  const setRemembered = (value) => {
    if (!CONFIG.rememberChoice || !value) return;
    try { sessionStorage.setItem(CONFIG.storageKey, value); } catch (e) { /* private mode */ }
  };

  // Set the select and let the app know about it
  function selectValue(select, value) {
    if (select.value === value) return;
    select.value = value;
    select.dispatchEvent(new Event('input',  { bubbles: true }));
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ------------------------------------------------------------------ */
  /* STYLES                                                              */
  /* ------------------------------------------------------------------ */

  function injectStyles() {
    if (document.getElementById('byob-options-styles')) return;
    const css = `
      /* One-choice dropdowns: hidden but still part of the form */
      .byob-bundle__product-variant-select.byob-single-choice { display: none !important; }

      /* Dropdowns replaced by buttons: visually hidden, still in the form */
      .byob-bundle__product-variant-select.byob-has-buttons {
        position: absolute !important;
        width: 1px; height: 1px;
        padding: 0; margin: -1px;
        overflow: hidden;
        clip: rect(0 0 0 0);
        white-space: nowrap;
        border: 0;
      }

      .byob-opt-buttons {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        width: 100%;
        margin: 4px 0 8px;
      }
      .byob-opt-buttons__btn {
        appearance: none;
        border: 1px solid ${CONFIG.border};
        background: #fff;
        color: #000;
        font: inherit;
        font-size: 14px;
        line-height: 1;
        min-width: 52px;
        padding: 12px 10px;
        border-radius: 4px;
        cursor: pointer;
        transition: background-color .15s ease, color .15s ease, border-color .15s ease;
      }
      .byob-opt-buttons__btn:hover:not([disabled]) { border-color: ${CONFIG.accent}; }
      .byob-opt-buttons__btn:focus-visible { outline: 2px solid ${CONFIG.accent}; outline-offset: 2px; }
      .byob-opt-buttons__btn[aria-pressed="true"] {
        background: ${CONFIG.accent};
        border-color: ${CONFIG.accent};
        color: #fff;
      }
      .byob-opt-buttons__btn[disabled] {
        opacity: .35;
        cursor: not-allowed;
        text-decoration: line-through;
      }

      /* ---------- Filter bar ---------- */
      .byob-filter {
        margin: 0 0 20px;
        padding: 12px 0;
        background: #fff;
        z-index: 5;
      }
      .byob-filter--sticky {
        position: sticky;
        top: ${CONFIG.stickyTop}px;
      }
      .byob-filter__search {
        display: block;
        width: 100%;
        box-sizing: border-box;
        font: inherit;
        font-size: 15px;
        padding: 12px 14px 12px 40px;
        margin: 0 0 12px;
        border: 1px solid ${CONFIG.border};
        border-radius: 999px;
        background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='none' stroke='%23808080' stroke-width='2' viewBox='0 0 24 24'%3E%3Ccircle cx='11' cy='11' r='7'/%3E%3Cpath d='m20 20-3.5-3.5'/%3E%3C/svg%3E") no-repeat 14px center;
        outline: none;
        transition: border-color .15s ease, box-shadow .15s ease;
      }
      .byob-filter__search:focus {
        border-color: ${CONFIG.accent};
        box-shadow: 0 0 0 3px ${CONFIG.accent}22;
      }
      .byob-filter__chips {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        overflow: hidden;
        transition: max-height .25s ease;
      }
      .byob-filter__chip {
        appearance: none;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        border: 1px solid ${CONFIG.border};
        background: #fff;
        color: #000;
        font: inherit;
        font-size: 14px;
        line-height: 1;
        padding: 10px 16px;
        border-radius: 999px;
        cursor: pointer;
        transition: background-color .15s ease, color .15s ease, border-color .15s ease;
      }
      .byob-filter__chip:hover { border-color: ${CONFIG.accent}; }
      .byob-filter__chip:focus-visible { outline: 2px solid ${CONFIG.accent}; outline-offset: 2px; }
      .byob-filter__chip[aria-pressed="true"] {
        background: ${CONFIG.accent};
        border-color: ${CONFIG.accent};
        color: #fff;
      }
      .byob-filter__chip-count { font-size: 12px; opacity: .7; }
      .byob-filter__label {
        font-size: 12px;
        font-weight: 600;
        letter-spacing: .04em;
        text-transform: uppercase;
        color: #808080;
        margin: 4px 0 6px;
      }
      .byob-filter__sizes {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 12px;
      }
      .byob-filter__chip--size {
        min-width: 48px;
        justify-content: center;
        padding: 10px 12px;
      }
      .byob-filter__row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-top: 10px;
        font-size: 13px;
        color: #808080;
      }
      .byob-filter__more, .byob-filter__clear {
        appearance: none;
        background: none;
        border: 0;
        padding: 0;
        font: inherit;
        font-size: 13px;
        color: ${CONFIG.accent};
        cursor: pointer;
        text-decoration: underline;
        text-underline-offset: 3px;
      }
      .byob-filter__more[hidden], .byob-filter__clear[hidden] { display: none; }
      .byob-filter-empty {
        padding: 40px 0;
        text-align: center;
        color: #808080;
        font-size: 15px;
      }

      /* ---------- Card polish ---------- */
      .byob-polish .byob-bundle__product-card {
        transition: transform .2s ease;
      }
      .byob-polish .byob-bundle__product-card-media {
        border-radius: 10px;
        overflow: hidden;
        transition: box-shadow .2s ease;
      }
      .byob-polish .byob-bundle__product-card-img {
        transition: transform .35s ease;
      }
      @media (hover: hover) {
        .byob-polish .byob-bundle__product-card:hover { transform: translateY(-3px); }
        .byob-polish .byob-bundle__product-card:hover .byob-bundle__product-card-media {
          box-shadow: 0 8px 24px rgba(0,0,0,.10);
        }
        .byob-polish .byob-bundle__product-card:hover .byob-bundle__product-card-img {
          transform: scale(1.04);
        }
      }
      .byob-card-hidden { display: none !important; }
    `;
    const style = document.createElement('style');
    style.id = 'byob-options-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  /* ------------------------------------------------------------------ */
  /* DECORATE ONE DROPDOWN                                               */
  /* ------------------------------------------------------------------ */

  function syncButtons(group, select) {
    group.querySelectorAll('.byob-opt-buttons__btn').forEach(btn => {
      btn.setAttribute('aria-pressed', String(btn.dataset.value === select.value));
    });
  }

  function decorateSelect(select) {
    const count = select.options.length;

    // 1. One choice only: hide it
    if (CONFIG.hideSingleChoice && count <= 1) {
      select.classList.add('byob-single-choice');
      return;
    }
    select.classList.remove('byob-single-choice');

    if (!CONFIG.buttons) return;

    // 2. Buttons. Placed right after the select so each dropdown gets its own.
    let group = select.nextElementSibling;
    if (!group || !group.classList.contains('byob-opt-buttons')) group = null;

    if (group && group.querySelectorAll('.byob-opt-buttons__btn').length === count) {
      syncButtons(group, select);
      return;
    }
    if (group) group.remove();

    group = document.createElement('div');
    group.className = 'byob-opt-buttons';
    group.setAttribute('role', 'group');
    group.setAttribute('aria-label', select.getAttribute('aria-label') || 'Options');

    Array.from(select.options).forEach(opt => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'byob-opt-buttons__btn';
      b.textContent = opt.textContent.trim();
      b.dataset.value = opt.value;
      if (opt.disabled) b.disabled = true;
      b.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (b.disabled) return;
        selectValue(select, opt.value);
        setRemembered(opt.value);
        syncButtons(group, select);
      });
      group.appendChild(b);
    });

    select.insertAdjacentElement('afterend', group);
    select.classList.add('byob-has-buttons');
    syncButtons(group, select);

    if (select.dataset.byobBound !== '1') {
      select.dataset.byobBound = '1';
      select.addEventListener('change', () => {
        const g = select.nextElementSibling;
        if (g && g.classList.contains('byob-opt-buttons')) syncButtons(g, select);
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* MODALS                                                              */
  /* ------------------------------------------------------------------ */

  function decorateDialog(dialog, applyRemembered) {
    const selects = dialog.querySelectorAll(SEL.select);
    selects.forEach(decorateSelect);

    // 3. Preselect the last size picked, if this product has it in stock
    if (!applyRemembered) return;
    const remembered = getRemembered();
    if (!remembered) return;
    selects.forEach(select => {
      if (select.options.length <= 1) return;
      const match = Array.from(select.options).find(o => o.value === remembered && !o.disabled);
      if (match) {
        selectValue(select, match.value);
        const g = select.nextElementSibling;
        if (g && g.classList.contains('byob-opt-buttons')) syncButtons(g, select);
        log('Preselected', remembered);
      }
    });
  }

  function watchDialog(dialog) {
    if (dialog.dataset.byobOptWatched === '1') return;
    dialog.dataset.byobOptWatched = '1';

    new MutationObserver(() => {
      if (dialog.open) decorateDialog(dialog, true);
    }).observe(dialog, { attributes: true, attributeFilter: ['open'] });

    decorateDialog(dialog, false);
  }

  const watchAll = () => document.querySelectorAll(SEL.dialog).forEach(watchDialog);

  /* ------------------------------------------------------------------ */
  /* FILTER BAR: STYLE CHIPS + SEARCH                                    */
  /* ------------------------------------------------------------------ */

  const membership = new Map();   // product handle -> Set of collection handles
  let chips = [];                 // [{ handle, label, count }] with count > 0
  const active = new Set();       // selected collection handles (empty = all)
  let activeSize = null;          // selected size (null = any)
  let sizeStock = null;           // product handle -> Set of in-stock sizes
  let sizeList = [];              // sizes in the store's order
  let query = '';
  let filtersReady = false;
  let expanded = false;

  // "/products/some-handle?variant=1" -> "some-handle"
  const handleFromHref = (href) => {
    if (!href) return null;
    const m = href.split('/products/')[1];
    return m ? m.split('?')[0].split('#')[0].toLowerCase() : null;
  };

  const cardHandle = (card) => {
    if (card.dataset.byobHandle) return card.dataset.byobHandle;
    const link = card.querySelector(SEL.detailsLink);
    const h = handleFromHref(link && link.getAttribute('href'));
    if (h) card.dataset.byobHandle = h;
    return h;
  };

  const cardTitle = (card) => {
    const el = card.querySelector(SEL.cardTitle);
    return (el ? el.textContent : card.textContent || '').trim().toLowerCase();
  };

  const allCards = () => Array.from(document.querySelectorAll(SEL.card));

  async function fetchCollection(handle) {
    const found = new Set();
    let title = null;
    // Collection title, for the chip label
    try {
      const r = await fetch(`/collections/${encodeURIComponent(handle)}.json`);
      if (r.ok) { const d = await r.json(); title = d && d.collection && d.collection.title; }
    } catch (e) { /* ignore */ }
    for (let page = 1; page <= 10; page++) {
      let data;
      try {
        const r = await fetch(`/collections/${encodeURIComponent(handle)}/products.json?limit=250&page=${page}`);
        if (!r.ok) { log('Collection not found:', handle, r.status); break; }
        data = await r.json();
      } catch (e) { log('Fetch failed for', handle, e); break; }
      const products = (data && data.products) || [];
      products.forEach(p => p.handle && found.add(p.handle.toLowerCase()));
      if (products.length < 250) break;
    }
    return { handle, title, found };
  }

  async function loadCollections() {
    const results = await Promise.all(CONFIG.collections.map(c => fetchCollection(c.handle)));
    const inGrid = new Set(allCards().map(cardHandle).filter(Boolean));

    chips = [];
    results.forEach((res, i) => {
      let count = 0;
      res.found.forEach(h => {
        if (!inGrid.has(h)) return;
        count++;
        if (!membership.has(h)) membership.set(h, new Set());
        membership.get(h).add(res.handle);
      });
      const label = CONFIG.collections[i].label || res.title || res.handle;
      if (count > 0) chips.push({ handle: res.handle, label, count });
      else log('No products from this bundle in collection:', res.handle);
    });
    filtersReady = true;
    log('Chips:', chips);
  }

  // Style chips from a product metafield. Each product in the grid is
  // looked up through /products/{handle}?view=byob-filter, which returns
  // {"value": ...} from the metafield set in that template.
  async function fetchMetafieldValue(handle) {
    try {
      // No Accept: application/json header: Shopify answers that with its
      // built-in product JSON and ignores ?view=
      const r = await fetch(`/products/${encodeURIComponent(handle)}?view=${encodeURIComponent(CONFIG.metafieldView)}`);
      if (!r.ok) return [];
      const text = (await r.text()).trim();
      if (!text.startsWith('{')) { log('Template not found or not JSON for', handle); return []; }
      const v = JSON.parse(text).value;
      if (v == null || v === '') return [];
      return (Array.isArray(v) ? v : [v]).map(x => String(x).trim()).filter(Boolean);
    } catch (e) {
      log('Metafield lookup failed for', handle, e);
      return [];
    }
  }

  async function loadMetafieldChips() {
    const handles = [...new Set(allCards().map(cardHandle).filter(Boolean))];
    const counts = new Map();

    // Look products up a few at a time
    const queue = handles.slice();
    const worker = async () => {
      while (queue.length) {
        const h = queue.shift();
        const values = await fetchMetafieldValue(h);
        values.forEach(v => {
          const key = 'mf:' + v;
          if (!membership.has(h)) membership.set(h, new Set());
          membership.get(h).add(key);
          counts.set(v, (counts.get(v) || 0) + 1);
        });
      }
    };
    await Promise.all(Array.from({ length: 6 }, worker));

    chips = [...counts.entries()]
      .map(([v, count]) => ({ handle: 'mf:' + v, label: CONFIG.metafieldLabels[v] || v, count }))
      .sort((a, b) => a.label.localeCompare(b.label));
    filtersReady = true;
    log('Metafield chips:', chips);
  }

  const loadStyleChips = () =>
    CONFIG.styleSource === 'metafield' ? loadMetafieldChips() : loadCollections();

  // Read in-stock sizes per product from <byob-product>/<byob-variant>,
  // which Simple Bundles renders on the page with an "available" flag.
  function buildSizeStock() {
    const map = new Map();
    const order = [];
    document.querySelectorAll('byob-product').forEach(p => {
      const h = handleFromHref(p.getAttribute('product-url'));
      if (!h) return;
      const names = (p.getAttribute('option-names') || '').split('<>').map(s => s.trim());
      let idx = names.findIndex(n => n.toLowerCase() === CONFIG.sizeOptionName.toLowerCase());
      if (idx === -1) return; // this product has no size option
      if (!map.has(h)) map.set(h, new Set());
      p.querySelectorAll('byob-variant').forEach(v => {
        const size = ((v.getAttribute('option-values') || '').split('<>')[idx] || '').trim();
        if (!size) return;
        if (!order.includes(size)) order.push(size);
        if (v.getAttribute('available') === 'true') map.get(h).add(size);
      });
    });
    sizeStock = map;
    sizeList = order;
    log('Sizes found:', sizeList);
  }

  function applyFilter() {
    const cards = allCards();
    let shown = 0;
    cards.forEach(card => {
      const h = cardHandle(card);
      const inCols = h ? membership.get(h) : null;
      const matchesChip = active.size === 0 ||
        (inCols && [...active].some(c => inCols.has(c)));
      const matchesSearch = !query || cardTitle(card).includes(query);
      const inStock = h && sizeStock ? sizeStock.get(h) : null;
      const matchesSize = !activeSize || (inStock && inStock.has(activeSize));
      const visible = matchesChip && matchesSearch && matchesSize;
      card.classList.toggle('byob-card-hidden', !visible);
      if (visible) shown++;
    });

    const bar = document.querySelector('.byob-filter');
    if (bar) {
      const status = bar.querySelector('.byob-filter__status');
      if (status) {
        status.textContent = (active.size || query || activeSize)
          ? `Showing ${shown} of ${cards.length}`
          : `${cards.length} products`;
      }
      const clear = bar.querySelector('.byob-filter__clear');
      if (clear) clear.hidden = !(active.size || query || activeSize);
      bar.querySelectorAll('.byob-filter__chip--size').forEach(chip => {
        chip.setAttribute('aria-pressed', String(chip.dataset.size === activeSize));
      });
      bar.querySelectorAll('.byob-filter__chip:not(.byob-filter__chip--size)').forEach(chip => {
        const c = chip.dataset.collection || null;
        chip.setAttribute('aria-pressed', String(c === null ? active.size === 0 : active.has(c)));
      });
    }

    // Empty state
    const grid = document.querySelector(SEL.grid);
    let empty = document.querySelector('.byob-filter-empty');
    if (grid && shown === 0) {
      if (!empty) {
        empty = document.createElement('p');
        empty.className = 'byob-filter-empty';
        empty.textContent = 'No products match. Try another style or search.';
        grid.insertAdjacentElement('afterend', empty);
      }
    } else if (empty) {
      empty.remove();
    }
  }

  // Collapse chips to N rows, show "+ More" only if they overflow
  function layoutChips(bar) {
    const wrap = bar.querySelector('.byob-filter__chips');
    const more = bar.querySelector('.byob-filter__more');
    const first = wrap && wrap.querySelector('.byob-filter__chip');
    if (!wrap || !more || !first) return;

    const gap = 8;
    const rowH = first.offsetHeight + gap;
    const collapsedH = rowH * CONFIG.rowsCollapsed - gap;

    wrap.style.maxHeight = 'none';
    const fullH = wrap.scrollHeight;

    if (fullH <= collapsedH + 2) {
      more.hidden = true;
      wrap.style.maxHeight = 'none';
      return;
    }
    more.hidden = false;
    wrap.style.maxHeight = (expanded ? fullH : collapsedH) + 'px';
    more.textContent = expanded ? '− Show less' : '+ More styles';
    more.setAttribute('aria-expanded', String(expanded));
  }

  function buildFilterBar() {
    const section = document.querySelector(SEL.section);
    const grid = document.querySelector(SEL.grid);
    if (!section || !grid) return;
    if (section.querySelector('.byob-filter')) return; // already there

    const bar = document.createElement('div');
    bar.className = 'byob-filter' + (CONFIG.sticky ? ' byob-filter--sticky' : '');

    if (CONFIG.search) {
      const input = document.createElement('input');
      input.type = 'search';
      input.className = 'byob-filter__search';
      input.placeholder = CONFIG.searchPlaceholder;
      input.setAttribute('aria-label', CONFIG.searchPlaceholder);
      input.value = query;
      input.addEventListener('input', debounce(() => {
        query = input.value.trim().toLowerCase();
        applyFilter();
      }, 150));
      bar.appendChild(input);
    }

    if (CONFIG.sizeFilter && sizeList.length) {
      const label = document.createElement('div');
      label.className = 'byob-filter__label';
      label.textContent = CONFIG.sizeLabel;
      bar.appendChild(label);

      const sizes = document.createElement('div');
      sizes.className = 'byob-filter__sizes';
      sizes.setAttribute('role', 'group');
      sizes.setAttribute('aria-label', 'Filter by size in stock');
      sizeList.forEach(size => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'byob-filter__chip byob-filter__chip--size';
        b.textContent = size;
        b.dataset.size = size;
        b.addEventListener('click', () => {
          activeSize = (activeSize === size) ? null : size;
          // Preselect this size in the product modals too
          if (activeSize) setRemembered(activeSize);
          applyFilter();
        });
        sizes.appendChild(b);
      });
      bar.appendChild(sizes);
    }

    if (CONFIG.filters && chips.length) {
      if (CONFIG.sizeFilter && sizeList.length) {
        const label = document.createElement('div');
        label.className = 'byob-filter__label';
        label.textContent = CONFIG.styleLabel;
        bar.appendChild(label);
      }
      const wrap = document.createElement('div');
      wrap.className = 'byob-filter__chips';
      wrap.setAttribute('role', 'group');
      wrap.setAttribute('aria-label', 'Filter by style');

      const makeChip = (label, handle, count) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'byob-filter__chip';
        b.textContent = label;
        if (handle) b.dataset.collection = handle;
        if (count) {
          const n = document.createElement('span');
          n.className = 'byob-filter__chip-count';
          n.textContent = count;
          b.appendChild(n);
        }
        b.addEventListener('click', () => {
          if (!handle) active.clear();
          else if (active.has(handle)) active.delete(handle);
          else active.add(handle);
          applyFilter();
        });
        return b;
      };

      wrap.appendChild(makeChip(CONFIG.allLabel, null, 0));
      chips.forEach(c => wrap.appendChild(makeChip(c.label, c.handle, c.count)));
      bar.appendChild(wrap);
    }

    const row = document.createElement('div');
    row.className = 'byob-filter__row';
    const status = document.createElement('span');
    status.className = 'byob-filter__status';
    const actions = document.createElement('span');

    const more = document.createElement('button');
    more.type = 'button';
    more.className = 'byob-filter__more';
    more.hidden = true;
    more.addEventListener('click', () => { expanded = !expanded; layoutChips(bar); });

    const clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'byob-filter__clear';
    clear.textContent = 'Clear filters';
    clear.hidden = true;
    clear.style.marginLeft = '16px';
    clear.addEventListener('click', () => {
      active.clear();
      activeSize = null;
      query = '';
      const input = bar.querySelector('.byob-filter__search');
      if (input) input.value = '';
      applyFilter();
    });

    actions.appendChild(more);
    actions.appendChild(clear);
    row.appendChild(status);
    row.appendChild(actions);
    bar.appendChild(row);

    section.insertBefore(bar, grid);
    layoutChips(bar);
    log('Filter bar added');
  }

  function setupFilters() {
    if (!CONFIG.filters && !CONFIG.search && !CONFIG.sizeFilter) return;
    if (CONFIG.sizeFilter && !sizeStock) buildSizeStock();
    buildFilterBar();
    applyFilter();
  }

  window.addEventListener('resize', debounce(() => {
    const bar = document.querySelector('.byob-filter');
    if (bar) layoutChips(bar);
  }, 150));

  /* ------------------------------------------------------------------ */
  /* BOOT + RE-APPLY ON RE-RENDER                                        */
  /* ------------------------------------------------------------------ */

  const refresh = debounce(() => {
    watchAll();
    document.querySelectorAll(SEL.dialog).forEach(d => decorateDialog(d, false));
    if (filtersReady) setupFilters();
    if (CONFIG.cardPolish) {
      const host = document.querySelector(SEL.host);
      if (host) host.classList.add('byob-polish');
    }
  }, 120);

  /* ------------------------------------------------------------------ */
  /* CART DRAWER AFTER "ADD BUNDLE TO CART"                              */
  /* ------------------------------------------------------------------ */

  // Simple Bundles adds the bundle to the cart, then sends the shopper to
  // /cart. We let the add happen untouched and only stop the page change
  // that follows, then refresh and open the theme's cart drawer instead.
  // Uses the browser's Navigation API; where that's missing, nothing
  // changes and the shopper goes to /cart as before.
  let lastAddClick = 0;

  function openThemeCartDrawer() {
    // Maestrooo: refresh the cart UI (Impact / Prestige v7+ / Stretch)
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

    setTimeout(() => {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.show === 'function') { drawer.show(); return true; }
      // Older Maestrooo themes: click the header cart button
      const toggle = document.querySelector('[aria-controls="cart-drawer"], [aria-label="Open cart"]');
      if (toggle) { toggle.click(); return true; }
      // No drawer found: go to the cart page so the shopper isn't left waiting
      window.location.href = '/cart';
    }, 150);
  }

  function setupCartDrawer() {
    if (!CONFIG.openCartDrawer || !window.navigation) {
      if (CONFIG.openCartDrawer) log('Navigation API not available, cart page used');
      return;
    }

    // Remember when the shopper presses "Add bundle to cart"
    document.addEventListener('click', (e) => {
      if (e.target.closest && e.target.closest('.byob-mini-cart__checkout')) lastAddClick = Date.now();
    }, true);

    window.navigation.addEventListener('navigate', (e) => {
      try {
        if (!lastAddClick || Date.now() - lastAddClick > 15000) return;
        if (!e.cancelable || e.formData) return; // never block a form post (e.g. /cart/add)
        const url = new URL(e.destination.url);
        if (url.origin !== window.location.origin) return;
        if (url.pathname.replace(/\/$/, '') !== '/cart') return; // only the cart page itself
        e.preventDefault();
        lastAddClick = 0;
        log('Opening cart drawer instead of /cart');
        openThemeCartDrawer();
      } catch (err) { /* let the navigation go ahead */ }
    });
  }

  function start(host) {
    injectStyles();
    if (CONFIG.cardPolish) host.classList.add('byob-polish');
    watchAll();
    setupCartDrawer();

    // Search and size work straight away; style chips are added once
    // the collections have loaded
    filtersReady = true;
    setupFilters();
    if (CONFIG.filters) {
      loadStyleChips().then(() => {
        const old = document.querySelector('.byob-filter');
        if (old) old.remove();   // rebuild with the chips
        setupFilters();
      });
    }

    new MutationObserver(refresh).observe(host, { childList: true, subtree: true });
    log('Initialised');
  }

  // Wait until the app has built the modals inside <byob-bundle>
  function waitForWidget(attempt = 0) {
    const host = document.querySelector(SEL.host);
    if (host && host.querySelector(SEL.dialog)) { start(host); return; }
    if (attempt > 60) { log('BYOB widget not found, giving up'); return; }
    setTimeout(() => waitForWidget(attempt + 1), 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => waitForWidget());
  } else {
    waitForWidget();
  }
})();